import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../../services/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { trackEsimEvent } from './esimAnalytics';
import { EsimInstallPanel } from './EsimInstallPanel';
import { formatInr, formatPackData } from './esimPlanFormat';
import { isIssuingOrder, pollDelayMs } from './esimOrderState';
import './EsimOrders.css';

const SUPPORT_URL = 'https://canadawaleyar.com';
const LIMIT = 3;

export interface EsimOrderSummary {
  id: string;
  status: string;
  plan?: string;
  dataGb?: number;
  days?: number;
  price?: number;
  orderRef?: string;
  failureCode?: string;
  createdAt?: string;
}

interface Props {
  onCount: (count: number) => void;
}

function purchasedOn(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function statusView(order: EsimOrderSummary): { label: string; tone: 'ok' | 'idle' | 'warn'; cta: string } {
  if (order.status === 'ready') return { label: 'Ready to install', tone: 'ok', cta: 'View QR' };
  if (order.status === 'payment_failed') return { label: 'Payment failed', tone: 'warn', cta: 'Details' };
  if (isIssuingOrder(order)) return { label: 'Pending', tone: 'idle', cta: 'Details' };
  return { label: 'Needs attention', tone: 'warn', cta: 'Details' };
}

function attentionText(order: EsimOrderSummary): string {
  const ref = order.orderRef ? ` Contact support with order ref ${order.orderRef}.` : '';
  if (order.status === 'payment_failed') return 'The payment did not go through. You have not been charged.';
  if (order.status === 'expired') return `This order expired.${ref}`;
  if (order.status === 'refunded') return `This order was refunded.${ref}`;
  return `We couldn’t finish setting up this eSIM.${ref}`;
}

export const EsimOrdersSection: React.FC<Props> = ({ onCount }) => {
  const location = useLocation();
  const navState = location.state as { openLatestReady?: boolean; openLatestPending?: boolean } | null;
  const openLatest = Boolean(navState?.openLatestReady);
  const openPending = Boolean(navState?.openLatestPending);
  const [orders, setOrders] = useState<EsimOrderSummary[]>([]);
  const [phase, setPhase] = useState<'idle' | 'error' | 'list'>('idle');
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [code, setCode] = useState('');
  const [detailTry, setDetailTry] = useState(0);
  const [detailError, setDetailError] = useState(false);
  const [live, setLive] = useState('');
  const seen = useRef(false);
  const autoOpened = useRef(false);
  const statuses = useRef<Record<string, string>>({});
  const preparing = orders.some((order) => isIssuingOrder(order));

  const load = async (kind: 'first' | 'retry' | 'poll') => {
    if (kind === 'retry') setRefreshing(true);
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.ESIM_ORDERS);
      const list = Array.isArray(data?.orders) ? data.orders as EsimOrderSummary[] : [];
      setOrders(list);
      setPhase(list.length > 0 ? 'list' : 'idle');
      onCount(list.length);
      if (list.length > 0 && !seen.current) {
        seen.current = true;
        trackEsimEvent({ name: 'orders_section_viewed', count: list.length });
      }
    } catch {
      if (kind !== 'poll') {
        setPhase('error');
        onCount(0);
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void load('first');
  }, []);

  useEffect(() => {
    if (!preparing || phase !== 'list') return;
    let timer = 0;
    let attempt = 0;
    let stopped = false;
    const tick = () => {
      if (stopped || document.hidden) return;
      timer = window.setTimeout(() => {
        void load('poll').finally(() => {
          attempt += 1;
          tick();
        });
      }, pollDelayMs(attempt));
    };
    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [preparing, phase]);

  useEffect(() => {
    if (autoOpened.current) return;
    const newest = openPending
      ? orders.find((order) => isIssuingOrder(order))
      : openLatest
        ? orders.find((order) => order.status === 'ready')
        : undefined;
    if (!newest) return;
    autoOpened.current = true;
    setOpenId(newest.id);
    trackEsimEvent({ name: 'order_opened', status: newest.status });
  }, [openLatest, openPending, orders]);

  useEffect(() => {
    for (const order of orders) {
      const previous = statuses.current[order.id];
      if (previous && previous !== 'ready' && order.status === 'ready') {
        setLive(`${order.plan || 'Your eSIM'} is ready to install`);
      }
      statuses.current[order.id] = order.status;
    }
  }, [orders]);

  const openStatus = orders.find((order) => order.id === openId)?.status;
  useEffect(() => {
    if (!openId || openStatus !== 'ready') {
      setCode('');
      setDetailError(false);
      return;
    }
    let cancel = false;
    setDetailError(false);
    axiosInstance.get(API_ENDPOINTS.ESIM_ORDER(openId))
      .then((res) => {
        const next = typeof res.data?.activationCode === 'string' ? res.data.activationCode : '';
        if (!cancel) setCode(next);
      })
      .catch(() => {
        if (!cancel) setDetailError(true);
      });
    return () => {
      cancel = true;
      setCode('');
    };
  }, [openId, openStatus, detailTry]);

  useEffect(() => () => { setCode(''); }, []);

  if (phase === 'idle' && !refreshing) return <p className="esim-orders-live" role="status" aria-live="polite">{live}</p>;

  const shown = showAll ? orders : orders.slice(0, LIMIT);
  const hidden = orders.length - LIMIT;

  const toggle = (order: EsimOrderSummary) => {
    setOpenId((current) => {
      const next = current === order.id ? null : order.id;
      if (next) trackEsimEvent({ name: 'order_opened', status: order.status });
      return next;
    });
  };

  return (
    <section className="esim-orders" id="your-esims" aria-labelledby="your-esims-title">
      <p className="esim-orders-live" role="status" aria-live="polite">{live}</p>
      <div className="esim-orders-head">
        <div>
          <p className="esim-eyebrow">Your purchases</p>
          <h2 id="your-esims-title">Your eSIMs</h2>
        </div>
        {phase === 'list' ? <span className="esim-orders-count">{orders.length}</span> : null}
      </div>
      <p className="esim-lead">Your QR codes stay here. Open one to install, download or copy it.</p>
      {phase === 'error' ? (
        <div className="esim-orders-error">
          <span>We couldn’t load your eSIMs.</span>
          <button type="button" className="esim-btn esim-btn-ghost" onClick={() => { void load('retry'); }}>Try again</button>
        </div>
      ) : null}
      {refreshing ? (
        <div className="esim-orders-list" aria-busy="true">
          <div className="esim-orders-skel" />
          <div className="esim-orders-skel" />
        </div>
      ) : null}
      {phase === 'list' && !refreshing ? (
        <>
          <ul className="esim-orders-list">
            {shown.map((order) => {
              const view = statusView(order);
              const open = openId === order.id;
              const figure = formatPackData({ dataGb: order.dataGb, name: order.plan });
              const when = purchasedOn(order.createdAt);
              const price = typeof order.price === 'number' ? formatInr(order.price) : '';
              const dataLabel = figure.value && figure.value !== '0' ? `${figure.value} ${figure.unit}` : '';
              const rows = [
                order.plan ? ['Plan', order.plan] : null,
                dataLabel ? ['Data', dataLabel] : null,
                order.days ? ['Validity', `${order.days} days`] : null,
                price ? [order.status === 'payment_failed' ? 'Amount' : 'Paid', price] : null,
                when ? ['Purchased', when] : null,
                order.orderRef ? ['Order ref', order.orderRef] : null,
              ].filter((row): row is [string, string] => Boolean(row));
              return (
                <li key={order.id} className={open ? 'esim-order is-open' : 'esim-order'}>
                  <button
                    type="button"
                    className="esim-order-head"
                    aria-expanded={open}
                    aria-controls={`esim-panel-${order.id}`}
                    id={`esim-head-${order.id}`}
                    onClick={() => toggle(order)}
                  >
                    <span className="esim-order-main">
                      <span className="esim-order-data">{figure.value}<small>{figure.unit}</small></span>
                      {order.plan ? <span className="esim-order-name">{order.plan}</span> : null}
                    </span>
                    <span className="esim-order-status">
                      <span className={`esim-order-pill is-${view.tone}`}>
                        {view.tone === 'idle' ? <span className="esim-order-spin" /> : null}
                        {view.tone === 'ok' ? <CheckIcon /> : null}
                        {view.tone === 'warn' ? <WarnIcon /> : null}
                        {view.label}
                      </span>
                      {when ? <span className="esim-order-when">Purchased {when}</span> : null}
                    </span>
                    <span className="esim-order-side">
                      {price ? <span className="esim-order-price">{price}</span> : null}
                      <span className="esim-order-view">{view.cta}<ChevronIcon /></span>
                    </span>
                  </button>
                  <div
                    className="esim-order-panel"
                    id={`esim-panel-${order.id}`}
                    role="region"
                    aria-labelledby={`esim-head-${order.id}`}
                    {...(open ? {} : { inert: true })}
                  >
                    <div className="esim-order-panel-inner">
                      {open && order.status === 'ready' && code ? (
                        <EsimInstallPanel
                          layout="split"
                          notice="short"
                          ready
                          activationCode={code}
                          planName={order.plan}
                          onLive={setLive}
                        >
                          <h3 className="esim-orders-details">Details</h3>
                          <dl className="esim-sum">
                            {rows.map(([label, value]) => (
                              <div className="esim-row" key={label}><dt>{label}</dt><dd className={label === 'Order ref' ? 'esim-mono' : undefined}>{value}</dd></div>
                            ))}
                          </dl>
                        </EsimInstallPanel>
                      ) : null}
                      {open && order.status === 'ready' && !code ? (
                        <div className="esim-orders-body">
                          <div className="esim-orders-qcol"><div className="esim-qr-tile">{detailError ? null : <div className="esim-skel" />}</div></div>
                          <div>
                            {detailError ? (
                              <div className="esim-orders-error">
                                <span>We couldn’t load this eSIM.</span>
                                <button type="button" className="esim-btn esim-btn-ghost" onClick={() => setDetailTry((value) => value + 1)}>Try again</button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {open && isIssuingOrder(order) ? (
                        <div className="esim-orders-body">
                          <div className="esim-orders-qcol"><div className="esim-qr-tile"><div className="esim-skel" /></div></div>
                          <div>
                            <h3 className="esim-orders-details">Details</h3>
                            <dl className="esim-sum">
                              {rows.map(([label, value]) => (
                                <div className="esim-row" key={label}><dt>{label}</dt><dd>{value}</dd></div>
                              ))}
                            </dl>
                            <p className="esim-notice"><span><b>Congratulations. Your payment was successful.</b> We are getting your eSIM ready. Please wait. You can leave this page. The QR will show here when it is ready.</span></p>
                          </div>
                        </div>
                      ) : null}
                      {open && !isIssuingOrder(order) && order.status !== 'ready' ? (
                        <div className="esim-orders-body">
                          <div className="esim-orders-qcol"><div className="esim-qr-tile esim-orders-noqr">No QR for this order</div></div>
                          <div>
                            <h3 className="esim-orders-details">Details</h3>
                            <dl className="esim-sum">
                              {rows.map(([label, value]) => (
                                <div className="esim-row" key={label}><dt>{label}</dt><dd>{value}</dd></div>
                              ))}
                            </dl>
                            <p className="esim-notice"><WarnIcon /><span>{attentionText(order)}</span></p>
                            <a className="esim-btn esim-btn-ghost esim-orders-support" href={SUPPORT_URL} onClick={() => trackEsimEvent({ name: 'support_clicked' })}>Contact support</a>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {!showAll && hidden > 0 ? (
            <div className="esim-orders-more">
              <button
                type="button"
                className="esim-btn esim-btn-ghost"
                onClick={() => {
                  setShowAll(true);
                  trackEsimEvent({ name: 'orders_show_older' });
                }}
              >
                Show {hidden} older
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

function ChevronIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>;
}
function CheckIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
}
function WarnIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17h.01" /></svg>;
}
